
import { ChangeDetectionStrategy, Component, computed, Input, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TwitchComment } from '../models/twitch.model';

@Component({
  selector: 'app-chat-overlay',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (isVisible()) {
      <div class="chat-overlay-inner absolute top-0 right-0 h-full w-1/2 sm:w-1/3 px-4 pt-4 pb-24 overflow-hidden pointer-events-none flex flex-col justify-end z-20 mask-image-gradient">
        <div class="flex flex-col justify-end min-h-0 space-y-2">
            @for (comment of visibleComments(); track comment.content_offset_seconds + '-' + comment.commenter.display_name + '-' + comment.message.body) {
                <div class="bg-black/40 text-gray-100 rounded-xl px-3 py-2 backdrop-blur-md shadow-sm border border-white/5 animate-fade-in-up origin-bottom-right self-end max-w-full">
                    <p class="text-[10px] font-bold text-indigo-300 mb-0.5 truncate">{{ comment.commenter.display_name }}</p>
                    <p class="text-xs break-words leading-snug">{{ comment.message.body }}</p>
                </div>
            }
        </div>
      </div>
    }
  `,
  styles: [`
    .mask-image-gradient {
        mask-image: linear-gradient(to bottom, transparent, black 10%);
        -webkit-mask-image: linear-gradient(to bottom, transparent, black 10%);
    }
    /* Overlay container plays a slide-in animation whenever it mounts (e.g. after being
       removed from DOM for the Twitch visibility check during play()). */
    .chat-overlay-inner {
        animation: slideInFromRight 220ms ease-out;
    }
    @keyframes slideInFromRight {
        from {
            transform: translateX(60%);
            opacity: 0;
        }
        to {
            transform: translateX(0);
            opacity: 1;
        }
    }
    .animate-fade-in-up {
        animation: fadeInUp 0.3s ease-out forwards;
    }
    @keyframes fadeInUp {
        from {
            opacity: 0;
            transform: translateY(10px) scale(0.95);
        }
        to {
            opacity: 1;
            transform: translateY(0) scale(1);
        }
    }
  `]
})
export class ChatOverlayComponent {
  private commentsSignal = signal<TwitchComment[]>([]);
  private currentTimeSignal = signal<number>(0);

  isVisible = signal<boolean>(true);

  @Input({ required: true })
  set comments(val: TwitchComment[]) {
    this.commentsSignal.set(val);
  }

  @Input({ required: true })
  set currentTime(val: number) {
    this.currentTimeSignal.set(val);
  }

  @Input()
  set visible(val: boolean) {
    this.isVisible.set(val);
  }

  visibleComments = computed(() => {
    const time = this.currentTimeSignal();
    const allComments = this.commentsSignal();

    if (allComments.length === 0) return [];

    const windowSize = 15; // Show messages from last 15 seconds
    const startTime = Math.max(0, time - windowSize);

    // 1. Binary search for the start index (first comment >= startTime)
    // O(log N) complexity - extremely fast even for 100k+ items
    let low = 0;
    let high = allComments.length - 1;
    let startIndex = -1;

    while (low <= high) {
      const mid = (low + high) >>> 1; // Unsigned right shift is slightly faster
      if (allComments[mid].content_offset_seconds >= startTime) {
        startIndex = mid;
        high = mid - 1;
      } else {
        low = mid + 1;
      }
    }

    if (startIndex === -1) return [];

    // 2. Linear scan from start index until we pass current time or hit limit
    const result: TwitchComment[] = [];
    const maxItems = 40; // Hard limit to prevent DOM overload during spam

    for (let i = startIndex; i < allComments.length; i++) {
        const comment = allComments[i];
        if (comment.content_offset_seconds > time) break;

        result.push(comment);

        // Safety brake
        if (result.length >= maxItems) {
            // If we have too many, keep the most recent ones (slice from end)
            return result.slice(-maxItems);
        }
    }

    return result;
  });
}
